"use client";

import { create } from "zustand";
import type {
  CreateWindowParams,
  WindowBounds,
  WindowId,
  WindowRecord,
  ToolType,
} from "./windowTypes";

export type WindowStoreState = {
  windows: Record<WindowId, WindowRecord>;
  order: WindowId[]; // top-most is last
  nextZ: number;
  createWindow: (params: CreateWindowParams) => WindowId;
  closeWindow: (id: WindowId) => void;
  focusWindow: (id: WindowId) => void;
  updateBounds: (id: WindowId, bounds: Partial<WindowBounds>) => void;
  minimizeWindow: (id: WindowId) => void;
  restoreWindow: (id: WindowId) => void;
  isOpen: (type: ToolType) => boolean;
  getByType: (type: ToolType) => WindowRecord | undefined;
  updateRotation: (id: WindowId, rotationDeg: number) => void;
};

function createId(): WindowId {
  return Math.random().toString(36).slice(2, 9);
}

const DEFAULT_BOUNDS: WindowBounds = { width: 360, height: 420, x: 80, y: 80 };

export const useWindowStore = create<WindowStoreState>((set, get) => ({
  windows: {},
  order: [],
  nextZ: 1,
  createWindow: ({
    type,
    title,
    initialBounds,
    minWidth,
    minHeight,
    isFrameless,
    isResizable,
    rotationDeg,
  }) => {
    const id = createId();
    const bounds: WindowBounds = {
      ...DEFAULT_BOUNDS,
      ...initialBounds,
    } as WindowBounds;
    const zIndex = get().nextZ;
    const record: WindowRecord = {
      id,
      type,
      title,
      zIndex,
      bounds,
      isFocused: true,
      isMinimized: false,
      minWidth,
      minHeight,
      isFrameless,
      isResizable,
      rotationDeg,
    };
    set((state) => ({
      windows: { ...state.windows, [id]: record },
      order: [...state.order.filter((w) => w !== id), id],
      nextZ: zIndex + 1,
    }));
    return id;
  },
  closeWindow: (id) => {
    set((state) => {
      const windows = { ...state.windows };
      delete windows[id];
      return { windows, order: state.order.filter((w) => w !== id) };
    });
  },
  focusWindow: (id) => {
    set((state) => {
      const win = state.windows[id];
      if (!win) return state;
      const zIndex = state.nextZ;
      return {
        windows: {
          ...state.windows,
          [id]: { ...win, zIndex, isFocused: true },
        },
        order: [...state.order.filter((w) => w !== id), id],
        nextZ: zIndex + 1,
      };
    });
  },
  updateBounds: (id, bounds) => {
    set((state) => {
      const win = state.windows[id];
      if (!win) return state;
      return {
        windows: {
          ...state.windows,
          [id]: { ...win, bounds: { ...win.bounds, ...bounds } },
        },
      };
    });
  },
  minimizeWindow: (id) => {
    set((state) => {
      const win = state.windows[id];
      if (!win) return state;
      return {
        windows: { ...state.windows, [id]: { ...win, isMinimized: true } },
      };
    });
  },
  restoreWindow: (id) => {
    set((state) => {
      const win = state.windows[id];
      if (!win) return state;
      return {
        windows: { ...state.windows, [id]: { ...win, isMinimized: false } },
      };
    });
  },
  isOpen: (type) => {
    return Object.values(get().windows).some((w) => w.type === type);
  },
  getByType: (type) => {
    return Object.values(get().windows).find((w) => w.type === type);
  },
  updateRotation: (id, rotationDeg) => {
    set((state) => {
      const win = state.windows[id];
      if (!win) return state;
      return {
        windows: { ...state.windows, [id]: { ...win, rotationDeg } },
      };
    });
  },
}));
